import { useEffect, useRef } from "react";

// A green follower dot that drags behind the pointer and eases to rest
// exactly on it when the mouse stops moving.
export const CursorDot = () => {
  const dotRef = useRef(null);

  useEffect(() => {
    // Skip on touch / no-hover devices
    if (window.matchMedia("(hover: none), (pointer: coarse)").matches) return;

    const dot = dotRef.current;
    if (!dot) return;

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const pos = { ...target };
    let visible = false;
    let raf;

    const isInteractive = (el) =>
      !!el &&
      (typeof el.closest === "function" &&
        el.closest('a, button, [role="button"], label, input, textarea, select, .social-btn'));

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
      if (!visible) {
        visible = true;
        pos.x = target.x;
        pos.y = target.y;
      }
      // Hide the dot when the pointer turns into a hand over links/buttons.
      dot.style.opacity = isInteractive(e.target) ? "0" : "1";
    };
    const onLeave = (e) => {
      // mouseout bubbles between elements; only hide when the pointer truly
      // leaves the window (relatedTarget is null on exit). This keeps the dot
      // smoothly trailing instead of snapping as it crosses nested elements.
      if (e.relatedTarget) return;
      visible = false;
      dot.style.opacity = "0";
    };
    const onDown = () => dot.classList.add("cursor-dot--down");
    const onUp = () => dot.classList.remove("cursor-dot--down");

    const tick = () => {
      // lerp: the smaller the factor, the more "drag" / smoother trailing
      pos.x += (target.x - pos.x) * 0.12;
      pos.y += (target.y - pos.y) * 0.12;
      dot.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
    // Attach pointer listeners + rAF loop once on mount. Re-running would leak
    // listeners and restart the animation loop. Intentional [].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={dotRef} className="cursor-dot" data-testid="cursor-dot" aria-hidden="true" />;
};
